/**
 * ApiKeysSection - account public API key management (Account -> API keys).
 * List + creation (name + scopes) + one-time display of the full key + revoke.
 * Gated by the `api.access` entitlement (Agency) -> otherwise an upsell card.
 *
 * The pieces live in `apiKeys/`; this file is the layout and the data.
 */
import { useEffect, useState } from "react";
import { KeyRound, Lock, Copy, Check, Plus, Terminal, BookOpen, ExternalLink } from "lucide-react";
import { Button, Card, CardContent, CardHeader, CardTitle, LoadingState } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { SITE_URL } from "../../lib/auth";
import { apiKeysApi, type ApiKey, type ApiScopeGroup } from "../../lib/apiKeys";
import { CreateKeyForm } from "./apiKeys/CreateKeyForm";
import { KeyRow } from "./apiKeys/KeyRow";

/**
 * The API reference on the marketing site. The Estonian slug is the base slug and
 * resolves in every locale; the English one is nicer in an English reader's URL bar.
 */
function docsUrl(locale: string): string {
  return `${SITE_URL}/${locale === "en" ? "developers" : "arendajatele"}`;
}

export function ApiKeysSection() {
  const t = useT();
  const docs = docsUrl(useI18n().locale);
  const [entitled, setEntitled] = useState<boolean | null>(null);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [groups, setGroups] = useState<ApiScopeGroup[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);

  async function reload() {
    try {
      const [list, sc] = await Promise.all([apiKeysApi.list(), apiKeysApi.scopes()]);
      setEntitled(list.entitled);
      setKeys(list.keys);
      setGroups(sc.groups ?? []);
    } catch {
      // Do not stay in the loading state forever: on the first error show the gate (lock),
      // on a later reload error keep the already known entitlement (do not throw the user out).
      setEntitled((prev) => (prev === null ? false : prev));
    }
  }
  useEffect(() => { void reload(); }, []);

  if (entitled === null) {
    return (
      <Card>
        <CardContent><LoadingState label={t("common.loading")} /></CardContent>
      </Card>
    );
  }

  if (entitled === false) {
    return (
      <Card>
        <CardContent className="py-8 text-center space-y-3">
          <Lock className="mx-auto size-6 text-muted-foreground" />
          <h3 className="text-sm font-semibold">{t("apiKeys.title")}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">{t("apiKeys.upsell")}</p>
          <div className="flex items-center justify-center gap-2">
            <Button size="sm" onClick={() => { window.location.search = "?tab=billing"; }}>{t("apiKeys.seePlans")}</Button>
            <Button size="sm" variant="outline" asChild>
              <a href={docs} target="_blank" rel="noreferrer">
                <BookOpen className="size-4" /> {t("apiKeys.docs")}
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="size-4" /> {t("apiKeys.title")}</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" asChild>
              <a href={docs} target="_blank" rel="noreferrer">
                <BookOpen className="size-4" /> {t("apiKeys.docs")}
              </a>
            </Button>
            <Button size="sm" onClick={() => { setFreshKey(null); setShowCreate((v) => !v); }}>
              <Plus className="size-4" /> {t("apiKeys.create")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("apiKeys.intro")}</p>

          {freshKey && (
            <div className="rounded-md border border-success/40 bg-success/5 p-3 space-y-2">
              <p className="text-xs font-medium text-success">{t("apiKeys.freshTitle")}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-background px-2 py-1 text-xs font-mono">{freshKey}</code>
                <CopyButton value={freshKey} t={t} />
              </div>
              <p className="text-[11px] text-muted-foreground">{t("apiKeys.freshHint")}</p>
            </div>
          )}

          {showCreate && (
            <CreateKeyForm
              t={t}
              groups={groups}
              onCreated={(full) => { setFreshKey(full); setShowCreate(false); void reload(); }}
            />
          )}

          {keys.length === 0 && !showCreate && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t("apiKeys.empty")}
            </div>
          )}

          <div className="space-y-2">
            {keys.map((k) => <KeyRow key={k.id} apiKey={k} t={t} onChange={() => void reload()} />)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-sm"><Terminal className="size-4" /> {t("apiKeys.usageTitle")}</CardTitle></CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 text-xs">
{`curl https://api.husly.app/api/v1/sites \\
  -H "Authorization: Bearer hsly_live_..."`}
          </pre>
          <a
            href={docs}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            {t("apiKeys.docsLink")} <ExternalLink className="size-3.5" />
          </a>
        </CardContent>
      </Card>
    </div>
  );
}

function CopyButton({ value, t }: { value: string; t: (k: string) => string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => { void navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />} {t("apiKeys.copy")}
    </Button>
  );
}
